/**
 * B632 (a) — the guest MAGIC LINK: a signed, single-use sign-in link mailed
 * to an order's buyer email, minting a durable CUSTOMER session bound to
 * that email's orders. RULED by the design seat 2026-10-06 under Pathik's
 * standing instruction (Pathik may overrule): no password, no account — the
 * inbox is the credential, exactly as it already is for the code+email door.
 *
 * Two tokens, one custody domain:
 *
 * - The LINK token (this module's own):
 *     mlk1.<base64url(canonical payload JSON)>.<keyId>.<base64url(signature)>
 *   signed with the GUEST-SESSION key (it mints sessions — same custody and
 *   blast radius as gst1; the distinct version string walls the two kinds:
 *   a gst1 verifier refuses mlk1 bytes and vice versa, because the version
 *   is inside the signed bytes). 15-minute TTL, SINGLE USE — the open door
 *   consumes its linkId atomically before any session leaves.
 *
 * - The SESSION it opens is an ordinary gst1 guest-session token (the
 *   authorizer resolves it into a CUSTOMER principal with ZERO new code),
 *   whose customerId is the EMAIL's own customer row (§4.1 create-or-reuse
 *   keys buyers by email, so the B25c fence over that id IS "every order
 *   whose buyer email matches" — no new fence, no wider one). 30-day TTL
 *   per the recorded default ("30-day refresh" read as a 30-day session;
 *   a fresh link is the refresh — recorded honest limit).
 *
 * Walls at the request door (all BEFORE any email leaves):
 * - origin wall (B31a §9.2) — same rule as session.guest, same rows;
 * - per-email + per-source rate caps counted against the append-only
 *   request log (the log IS the audit trail; fail closed when a source
 *   arrives and no salted hasher is wired — B103's stance);
 * - UNIFORM ANSWER: an unknown or malformed email answers exactly like a
 *   known one ({ ok: true }, nothing sent) — the door never says whether
 *   an address has bookings.
 *
 * PURITY: signing, verification, randomness, clock and mail are injected.
 */

import type { ChannelConfigStore } from "./channel-config.js";
import type { Clock } from "./clock.js";
import { InvalidCustomerEmailError, normalizeCustomerEmail, type CustomerStore } from "./customers.js";
import type { EmailSenderPort } from "./delivery.js";
import { checkEmbedOrigin } from "./embedding.js";
import {
  mintGuestSessionToken,
  type GuestMintOriginRefusal,
} from "./guest-session.js";
import { tenantRowKey } from "./keys.js";
import type { OpsConfigStore } from "./ops-config.js";
import type { TenantDirectoryStore } from "./tenant-directory.js";
import { emailDocument, escapeHtml } from "./ticket-email.js";
import { ticketEmailSenderName } from "./ticket-email-settings.js";
import { fromBase64Url, toBase64Url, type TicketSigner, type TicketVerifierPort } from "./ticket-token.js";

/* ---------------------------------------------------------------- types */

export interface MagicLinkPayload {
  readonly tenantId: string;
  readonly channelId: string;
  /** Normalized (trimmed, lowercased) — the bytes signed are the bytes compared. */
  readonly email: string;
  /** The single-use claim's id; consuming it is what spends the link. */
  readonly linkId: string;
  readonly issuedAt: number;
  readonly expiresAt: number;
}

export type MagicLinkTokenRefusal = "malformed" | "bad_signature" | "unknown_key" | "expired";

export type VerifiedMagicLink =
  | { readonly ok: true; readonly keyId: string; readonly payload: MagicLinkPayload }
  | { readonly ok: false; readonly why: MagicLinkTokenRefusal };

export class InvalidMagicLinkPayloadError extends Error {
  constructor(why: string) {
    super(`Refusing to mint a magic link: ${why}`);
    this.name = "InvalidMagicLinkPayloadError";
  }
}

/** The flood wall spoke. The wire answer stays uniform; the dimension is for OUR logs. */
export class MagicLinkRateLimitedError extends Error {
  constructor(
    tenantId: string,
    readonly dimension: "email" | "source",
  ) {
    super(`Magic-link request rate limit reached: tenant=${tenantId} dimension=${dimension}; retry later`);
    this.name = "MagicLinkRateLimitedError";
  }
}

/** B103's stance: a request carrying a transport source with no salted hasher wired refuses (fail closed). */
export class MagicLinkSourceWallUnwiredError extends Error {
  constructor(tenantId: string) {
    super(`Magic-link request refused: source wall unwired: tenant=${tenantId}`);
    this.name = "MagicLinkSourceWallUnwiredError";
  }
}

/** B31a §9.2 — the origin wall said no (same reasons vocabulary as the guest mint). */
export class MagicLinkOriginRefusedError extends Error {
  constructor(
    tenantId: string,
    channelId: string,
    readonly reason: GuestMintOriginRefusal,
  ) {
    super(`Magic-link request refused by the origin wall: tenant=${tenantId} channel=${channelId} reason=${reason}`);
    this.name = "MagicLinkOriginRefusedError";
  }
}

/**
 * The open door said no. ONE wire shape for every reason — a spent link, an
 * expired one, a forged one and a never-minted one are indistinguishable to
 * the caller (the `why` is for OUR logs, never the message).
 */
export class MagicLinkOpenRefusedError extends Error {
  constructor(
    readonly why: MagicLinkTokenRefusal | "consumed" | "no_customer" | "tenant_mismatch",
  ) {
    super(`Magic link refused: ${why}`);
    this.name = "MagicLinkOpenRefusedError";
  }
}

/* ------------------------------------------------------------ the stores */

/** One row per request ATTEMPT that passed the walls — the audit trail the rate limit counts. */
export interface MagicLinkRequestRecord {
  readonly tenantId: string;
  readonly channelId: string;
  /** Normalized email. */
  readonly email: string;
  readonly linkId: string;
  readonly requestId: string;
  readonly at: number;
  /** Whether a mail actually left (false = unknown address; the wire never says). */
  readonly sent: boolean;
  /** B103 — the SALTED HASH of the transport source; absent for sourceless callers. */
  readonly sourceKey?: string;
}

export interface MagicLinkRequestLogStore {
  /** Append-only; no update or delete path exists. */
  append(record: MagicLinkRequestRecord): Promise<void>;
  /** Requests for (tenant, email) with at >= since — the per-email window count. */
  countByEmailSince(tenantId: string, email: string, since: number): Promise<number>;
  /** Requests for (tenant, sourceKey) with at >= since — the per-source window count. */
  countBySourceSince(tenantId: string, sourceKey: string, since: number): Promise<number>;
}

export class InMemoryMagicLinkRequestLogStore implements MagicLinkRequestLogStore {
  private readonly rows = new Map<string, MagicLinkRequestRecord[]>();

  append(record: MagicLinkRequestRecord): Promise<void> {
    const key = tenantRowKey(record.tenantId, "mlk-req");
    const list = this.rows.get(key) ?? [];
    list.push(record);
    this.rows.set(key, list);
    return Promise.resolve();
  }

  countByEmailSince(tenantId: string, email: string, since: number): Promise<number> {
    const list = this.rows.get(tenantRowKey(tenantId, "mlk-req")) ?? [];
    return Promise.resolve(list.filter((r) => r.email === email && r.at >= since).length);
  }

  countBySourceSince(tenantId: string, sourceKey: string, since: number): Promise<number> {
    const list = this.rows.get(tenantRowKey(tenantId, "mlk-req")) ?? [];
    return Promise.resolve(list.filter((r) => r.sourceKey === sourceKey && r.at >= since).length);
  }
}

/** The single-use claim: `consume` answers true exactly once per (tenant, linkId). */
export interface MagicLinkConsumedStore {
  consume(tenantId: string, linkId: string, at: number): Promise<boolean>;
}

/**
 * B1085 (4b) — READ a single-use claim without spending it: true once
 * `consume` has run for (tenant, id). The UCP `get_checkout` read answers
 * not_found the moment a person adopts the handed-off cart, and adoption is
 * exactly this claim being spent. A separate interface so every existing
 * consumer of the store keeps its one-method shape.
 */
export interface ConsumedClaimReader {
  spent(tenantId: string, linkId: string): Promise<boolean>;
}

export class InMemoryMagicLinkConsumedStore implements MagicLinkConsumedStore, ConsumedClaimReader {
  private readonly claims = new Set<string>();

  consume(tenantId: string, linkId: string, _at: number): Promise<boolean> {
    const key = tenantRowKey(tenantId, linkId);
    if (this.claims.has(key)) return Promise.resolve(false);
    this.claims.add(key);
    return Promise.resolve(true);
  }

  spent(tenantId: string, linkId: string): Promise<boolean> {
    return Promise.resolve(this.claims.has(tenantRowKey(tenantId, linkId)));
  }
}

/* -------------------------------------------------------------- payload */

const PAYLOAD_KEYS = ["tenantId", "channelId", "email", "linkId", "issuedAt", "expiresAt"] as const;
const VERSION = "mlk1";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function assertPayload(payload: MagicLinkPayload): void {
  for (const field of ["tenantId", "channelId", "email", "linkId"] as const) {
    const v = payload[field];
    if (typeof v !== "string" || v.trim().length === 0)
      throw new InvalidMagicLinkPayloadError(`${field} is required`);
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = payload[field];
    if (!Number.isSafeInteger(v) || v < 0)
      throw new InvalidMagicLinkPayloadError(`${field} must be a non-negative epoch-ms integer`);
  }
  if (payload.expiresAt <= payload.issuedAt)
    throw new InvalidMagicLinkPayloadError("expiresAt must follow issuedAt");
}

/** Canonical form: fixed key order — deterministic bytes (ticket-token discipline). */
function canonicalPayloadJson(payload: MagicLinkPayload): string {
  const ordered: Record<string, unknown> = {};
  for (const key of PAYLOAD_KEYS) ordered[key] = payload[key];
  return JSON.stringify(ordered);
}

function parsePayload(json: string): MagicLinkPayload | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(PAYLOAD_KEYS as readonly string[]).includes(key)) return undefined; // extras are malformed
  }
  const out: Record<string, unknown> = {};
  for (const field of ["tenantId", "channelId", "email", "linkId"] as const) {
    const v = record[field];
    if (typeof v !== "string" || v.trim().length === 0) return undefined;
    out[field] = v;
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = record[field];
    if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) return undefined;
    out[field] = v;
  }
  const payload = out as unknown as MagicLinkPayload;
  if (payload.expiresAt <= payload.issuedAt) return undefined;
  return payload;
}

/* ------------------------------------------------------- token mechanics */

export async function mintMagicLinkToken(payload: MagicLinkPayload, signer: TicketSigner): Promise<string> {
  assertPayload(payload);
  if (signer.keyId.trim().length === 0 || signer.keyId.includes("."))
    throw new InvalidMagicLinkPayloadError("signer keyId must be non-empty and dot-free");
  const body = toBase64Url(encoder.encode(canonicalPayloadJson(payload)));
  const signedBytes = encoder.encode(`${VERSION}.${body}.${signer.keyId}`);
  const signature = await signer.sign(signedBytes);
  return `${VERSION}.${body}.${signer.keyId}.${toBase64Url(signature)}`;
}

/** Structural parse — fail-closed, throw-free, canonical-form-only. */
export function parseMagicLinkToken(token: string):
  | { readonly ok: true; readonly payload: MagicLinkPayload; readonly keyId: string; readonly signedBytes: Uint8Array; readonly signature: Uint8Array }
  | { readonly ok: false; readonly why: "malformed" } {
  const malformed = { ok: false as const, why: "malformed" as const };
  const parts = token.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const payloadBytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (payloadBytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const payload = parsePayload(decoder.decode(payloadBytes));
  if (payload === undefined) return malformed;
  if (toBase64Url(encoder.encode(canonicalPayloadJson(payload))) !== body) return malformed;
  return { ok: true, payload, keyId, signedBytes: encoder.encode(`${VERSION}.${body}.${keyId}`), signature };
}

/** Full verification: structure, signature under the named key, expiry against the caller's clock. */
export async function verifyMagicLinkToken(
  token: string,
  verifier: TicketVerifierPort,
  clock: Clock,
): Promise<VerifiedMagicLink> {
  const parsed = parseMagicLinkToken(token);
  if (!parsed.ok) return { ok: false, why: parsed.why };
  const result = await verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  if (result !== "ok") return { ok: false, why: result };
  if (clock.now() >= parsed.payload.expiresAt) return { ok: false, why: "expired" };
  return { ok: true, keyId: parsed.keyId, payload: parsed.payload };
}

/* ---------------------------------------------------------------- email */

/** The link rides the FRAGMENT (`/me#<token>`) — the ticket page's own discipline: a fragment never reaches server logs. */
export function magicLinkUrl(base: string, token: string): string {
  return `${base.replace(/\/+$/, "")}/me#${token}`;
}

export interface MagicLinkEmailView {
  /** The tenant's trading name; absent = the platform's plain words. */
  readonly businessName?: string;
  readonly url: string;
  /** How long the link lives, in whole minutes — the email says so honestly. */
  readonly minutes: number;
}

/**
 * B606 discipline: a 600px table with inline CSS only, every cell painting
 * its own background, no external anything, every tenant string escaped.
 * The token appears ONLY inside the link URL.
 */
export function renderMagicLinkEmail(view: MagicLinkEmailView): {
  readonly subject: string;
  readonly textBody: string;
  readonly htmlBody: string;
} {
  const name = view.businessName?.trim();
  const subject = name === undefined || name === "" ? "Your bookings" : `Your bookings — ${name}`;
  const whose = name === undefined || name === "" ? "your bookings" : `your bookings with ${name}`;
  const textBody = [
    `Here is the sign-in link you asked for to see ${whose}:`,
    "",
    view.url,
    "",
    `The link works once and expires in ${view.minutes} minutes.`,
    "If you didn't ask for it, you can ignore this email — nothing changes.",
  ].join("\n");
  const safeName = escapeHtml(name ?? "");
  const safeUrl = escapeHtml(view.url);
  const content = [
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;margin:0;padding:0;">',
    '<tr><td align="center" style="background-color:#f4f4f5;padding:24px 12px;">',
    '<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:100%;background-color:#ffffff;border-radius:8px;">',
    `<tr><td style="background-color:#ffffff;padding:32px 32px 8px 32px;font-family:Arial,Helvetica,sans-serif;font-size:20px;font-weight:bold;color:#18181b;">${
      safeName === "" ? "Your bookings" : safeName
    }</td></tr>`,
    `<tr><td style="background-color:#ffffff;padding:8px 32px;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:22px;color:#3f3f46;">Here is the sign-in link you asked for to see ${
      safeName === "" ? "your bookings" : `your bookings with ${safeName}`
    }.</td></tr>`,
    `<tr><td style="background-color:#ffffff;padding:16px 32px;"><a href="${safeUrl}" style="display:inline-block;background-color:#18181b;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;padding:12px 24px;border-radius:6px;">Open my bookings</a></td></tr>`,
    `<tr><td style="background-color:#ffffff;padding:8px 32px 32px 32px;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:18px;color:#71717a;">The link works once and expires in ${view.minutes} minutes. If you didn&#39;t ask for it, you can ignore this email — nothing changes.</td></tr>`,
    "</table></td></tr></table>",
  ].join("");
  const htmlBody = emailDocument({ title: subject, background: "#f4f4f5", content });
  return { subject, textBody, htmlBody };
}

/* ---------------------------------------------------------- orchestration */

/** 15 minutes — the recorded default's own number. */
export const DEFAULT_MAGIC_LINK_TTL_MS = 15 * 60_000;
/** The session the link opens — the recorded default's 30 days. */
export const DEFAULT_MAGIC_SESSION_TTL_MS = 30 * 24 * 60 * 60_000;
export const DEFAULT_MAGIC_LINK_WINDOW_MS = 60 * 60_000;
/** A real buyer asks a handful of times; 5/h per address keeps an inbox un-bombable. */
export const DEFAULT_MAGIC_LINK_MAX_PER_EMAIL_PER_WINDOW = 5;
/** Mirrors the guest mint's per-source stance (B103). */
export const DEFAULT_MAGIC_LINK_MAX_PER_SOURCE_PER_WINDOW = 30;

export interface MagicLinkDeps {
  readonly requestLog: MagicLinkRequestLogStore;
  readonly consumed: MagicLinkConsumedStore;
  readonly customers: Pick<CustomerStore, "getByEmail">;
  /** The GUEST-SESSION signing key (same custody domain: both mint sessions). */
  readonly signer: TicketSigner;
  /** Verification for the open door; absent = open refuses (fail closed). */
  readonly verifier?: TicketVerifierPort;
  readonly clock: Clock;
  /** Fresh, unguessable link id — crypto randomness is I/O, minted at the edge. */
  readonly newLinkId: () => string;
  readonly email: EmailSenderPort;
  /** The storefront base the link lands on (`<base>/me#<token>`). */
  readonly linkUrlBase?: string;
  /** B31a §9.2 — the same rows channel.config.set writes; origin-carrying requests refuse when unwired. */
  readonly channelConfigs?: Pick<ChannelConfigStore, "get">;
  /** B606 (f) — the tenant's sender name + reply-to ride the same rows the ticket email reads. */
  readonly opsConfig?: Pick<OpsConfigStore, "get">;
  readonly tenants?: Pick<TenantDirectoryStore, "get">;
  /** B103 — the stage-salted source hasher; unwired + a source present = refuse. */
  readonly sourceKeyOf?: (source: string) => string;
  readonly linkTtlMs?: number;
  readonly sessionTtlMs?: number;
  readonly rateWindowMs?: number;
  readonly maxPerEmailPerWindow?: number;
  readonly maxPerSourcePerWindow?: number;
}

export interface RequestMagicLinkCommand {
  readonly tenantId: string;
  readonly channelId: string;
  readonly email: string;
  readonly requestId: string;
  readonly origin?: string;
  readonly source?: string;
}

/**
 * The request flow: origin wall, source hash (fail closed), rate caps
 * counted against the log, append the attempt row, and ONLY THEN — when the
 * address has a customer row — mint and mail. The wire answer is `{}`-shaped
 * uniformity: the caller learns nothing about the address either way.
 */
export async function requestMagicLink(deps: MagicLinkDeps, cmd: RequestMagicLinkCommand): Promise<{ readonly ok: true }> {
  if (cmd.tenantId.trim().length === 0) throw new InvalidMagicLinkPayloadError("tenantId is required");
  if (cmd.channelId.trim().length === 0) throw new InvalidMagicLinkPayloadError("channelId is required");

  // B31a §9.2 — the origin wall FIRST, the guest mint's exact stance.
  if (cmd.origin !== undefined) {
    if (deps.channelConfigs === undefined) {
      throw new MagicLinkOriginRefusedError(cmd.tenantId, cmd.channelId, "ORIGIN_WALL_UNWIRED");
    }
    const configRow = await deps.channelConfigs.get(cmd.tenantId, cmd.channelId);
    if (configRow === undefined) {
      throw new MagicLinkOriginRefusedError(cmd.tenantId, cmd.channelId, "CHANNEL_NOT_CONFIGURED");
    }
    const verdict = checkEmbedOrigin(configRow.config, cmd.origin);
    if (!verdict.allowed) throw new MagicLinkOriginRefusedError(cmd.tenantId, cmd.channelId, verdict.reason);
  }

  // B103 — hash the transport source before anything is counted (fail closed).
  let sourceKey: string | undefined;
  if (cmd.source !== undefined) {
    if (deps.sourceKeyOf === undefined) throw new MagicLinkSourceWallUnwiredError(cmd.tenantId);
    sourceKey = deps.sourceKeyOf(cmd.source);
    if (sourceKey.length === 0) throw new InvalidMagicLinkPayloadError("sourceKey must be non-empty");
  }

  // A malformed address can never match a customer row: uniform ok, nothing
  // sent, nothing logged (no email leaves, so there is nothing to flood).
  let email: string;
  try {
    email = normalizeCustomerEmail(cmd.email);
  } catch (err) {
    if (err instanceof InvalidCustomerEmailError) return { ok: true };
    throw err;
  }

  const now = deps.clock.now();
  const windowMs = deps.rateWindowMs ?? DEFAULT_MAGIC_LINK_WINDOW_MS;
  const emailCap = deps.maxPerEmailPerWindow ?? DEFAULT_MAGIC_LINK_MAX_PER_EMAIL_PER_WINDOW;
  const sourceCap = deps.maxPerSourcePerWindow ?? DEFAULT_MAGIC_LINK_MAX_PER_SOURCE_PER_WINDOW;

  if (sourceKey !== undefined) {
    const recentBySource = await deps.requestLog.countBySourceSince(cmd.tenantId, sourceKey, now - windowMs);
    if (recentBySource >= sourceCap) throw new MagicLinkRateLimitedError(cmd.tenantId, "source");
  }
  const recentByEmail = await deps.requestLog.countByEmailSince(cmd.tenantId, email, now - windowMs);
  if (recentByEmail >= emailCap) throw new MagicLinkRateLimitedError(cmd.tenantId, "email");

  // The address's customer row decides whether a mail leaves; the answer never does.
  const row = await deps.customers.getByEmail(cmd.tenantId, email);
  const linkId = `mlk-${deps.newLinkId()}`;

  // The attempt row precedes the mail — a request that cannot be audited
  // does not send (the guest mint's own ordering).
  await deps.requestLog.append({
    tenantId: cmd.tenantId,
    channelId: cmd.channelId,
    email,
    linkId,
    requestId: cmd.requestId,
    at: now,
    sent: row !== undefined,
    ...(sourceKey === undefined ? {} : { sourceKey }),
  });
  if (row === undefined) return { ok: true };

  const ttlMs = deps.linkTtlMs ?? DEFAULT_MAGIC_LINK_TTL_MS;
  const token = await mintMagicLinkToken(
    { tenantId: cmd.tenantId, channelId: cmd.channelId, email, linkId, issuedAt: now, expiresAt: now + ttlMs },
    deps.signer,
  );
  const url = magicLinkUrl(deps.linkUrlBase ?? "https://tickets.invalid", token);

  // B606 (f) — the tenant's own sender name and reply-to, best-effort.
  let businessName: string | undefined;
  let replyTo: string | undefined;
  try {
    const opsRow = await deps.opsConfig?.get(cmd.tenantId);
    const tenantName = (await deps.tenants?.get(cmd.tenantId))?.name;
    businessName = ticketEmailSenderName(opsRow?.config.ticketEmail, tenantName) ?? opsRow?.config.businessName ?? tenantName;
    const contact = opsRow?.config.contactEmail?.trim();
    replyTo = contact === undefined || contact === "" ? undefined : contact;
  } catch {
    businessName = undefined;
    replyTo = undefined;
  }

  const rendered = renderMagicLinkEmail({ url, minutes: Math.max(1, Math.round(ttlMs / 60_000)), ...(businessName === undefined ? {} : { businessName }) });
  await deps.email.send({
    to: email,
    subject: rendered.subject,
    textBody: rendered.textBody,
    htmlBody: rendered.htmlBody,
    trace: { tenantId: cmd.tenantId, deliveryId: linkId },
    ...(businessName === undefined ? {} : { fromName: businessName }),
    ...(replyTo === undefined ? {} : { replyTo }),
  });
  return { ok: true };
}

export interface OpenMagicLinkCommand {
  readonly tenantId: string;
  readonly token: string;
}

export interface OpenedMagicSession {
  /** An ordinary gst1 CUSTOMER session token — the authorizer resolves it unchanged. */
  readonly token: string;
  readonly customerId: string;
  readonly email: string;
  readonly expiresAt: number;
}

/**
 * The open flow: verify (structure, signature, expiry), consume the single
 * use ATOMICALLY (losers of a double-click race refuse), resolve the email's
 * customer row, mint the session. Every refusal is the ONE
 * MagicLinkOpenRefusedError — a probe learns nothing about which wall spoke.
 */
export async function openMagicLink(deps: MagicLinkDeps, cmd: OpenMagicLinkCommand): Promise<OpenedMagicSession> {
  if (deps.verifier === undefined) throw new MagicLinkOpenRefusedError("bad_signature"); // unwired = fail closed
  const verified = await verifyMagicLinkToken(cmd.token, deps.verifier, deps.clock);
  if (!verified.ok) throw new MagicLinkOpenRefusedError(verified.why);
  const payload = verified.payload;
  // The door's tenant claim and the signed payload's must agree (Inv 10).
  if (payload.tenantId !== cmd.tenantId) throw new MagicLinkOpenRefusedError("tenant_mismatch");

  const now = deps.clock.now();
  const fresh = await deps.consumed.consume(payload.tenantId, payload.linkId, now);
  if (!fresh) throw new MagicLinkOpenRefusedError("consumed");

  const row = await deps.customers.getByEmail(payload.tenantId, payload.email);
  if (row === undefined) throw new MagicLinkOpenRefusedError("no_customer");

  const expiresAt = now + (deps.sessionTtlMs ?? DEFAULT_MAGIC_SESSION_TTL_MS);
  const token = await mintGuestSessionToken(
    {
      tenantId: payload.tenantId,
      channelId: payload.channelId,
      customerId: row.customer.customerId,
      issuedAt: now,
      expiresAt,
    },
    deps.signer,
  );
  return { token, customerId: row.customer.customerId, email: payload.email, expiresAt };
}
